<template>
  <div class="h-full">
    <TOverlay
      class="z-30"
      :fixed="false"
      :show="[STATES.LOADING, STATES.SENDING].includes(currentState)"
    />

    <UAlert
      v-if="currentState === STATES.SEND_ERROR"
      class="mb-4"
      color="error"
      variant="subtle"
      :title="addCottageError || t('outings.favorite_modal.error_add')"
    />

    <UAlert
      v-if="currentState === STATES.SUCCESS"
      class="mb-4"
      color="primary"
      variant="subtle"
      :title="t('outings.favorite_modal.success_title')"
      :description="t('outings.favorite_modal.success_description')"
    />

    <template v-if="outingsList && outingsList.length === 0">
      <TTitle size="2xl" classes="mb-6" leading="tight">
        {{ t('outings.favorite_modal.add_modal_title') }}
      </TTitle>

      <TAlert kind="blue">
        <template #title>
          {{ t('outings.favorite_modal.empty_title') }}
        </template>
        <template #text>
          {{ t('outings.favorite_modal.empty_description') }}
        </template>
      </TAlert>

      <UButton
        class="mt-4"
        :to="routes().outingPlanner({ cottageUid: item.id })"
        block
        color="primary"
        variant="solid"
        icon="i-heroicons-plus"
      >
        {{ t('outings.common.create') }}
      </UButton>
    </template>

    <template v-else-if="outingsList && currentState !== STATES.SUCCESS">
      <TTitle size="2xl" classes="mb-6" leading="tight">
        {{ t('outings.favorite_modal.add_modal_title') }}
      </TTitle>

      <div class="space-y-2 mb-4">
        <button
          v-for="outing in outingsList"
          :key="outing.id"
          class="w-full p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="currentState === STATES.SENDING"
          @click="handleSelectOuting(outing)"
        >
          <div class="flex items-center justify-between">
            <div>
              <div class="font-semibold text-gray-900">
                {{ outing.name }}
              </div>
              <div
                v-if="outing.dateIn && outing.dateOut"
                class="text-sm text-gray-600"
              >
                {{ formatDates(outing.dateIn, outing.dateOut) }}
              </div>
            </div>
            <UIcon name="i-heroicons-chevron-right" class="text-gray-400" />
          </div>
        </button>
      </div>

      <UButton
        :to="routes().outingPlanner({ cottageUid: item.id })"
        block
        color="primary"
        icon="i-heroicons-plus"
        size="lg"
      >
        {{ t('outings.common.create') }}
      </UButton>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from '~~/modules/auth/stores/userStore'
import { browserFetch as getOutingsList } from '~~/modules/api/rurality/v3/outings/outings/list'
import { browserFetch as addCottageToOuting } from '~~/modules/api/rurality/v3/outings/cottages/add'
import type { CottagePage } from '../entities/CottagePage'

type itemType = Pick<
  CottagePage,
  'media' | 'name' | 'village' | 'province' | 'id'
>

interface PropsInterface {
  item: itemType
}

const props = defineProps<PropsInterface>()
const emit = defineEmits(['submitSuccess'])

const userStore = useUserStore()
const outingsList = ref<any[] | null>(null)
const addCottageError = ref<string | null>(null)

// State Machine
const STATES = {
  LOADING: 'LOADING',
  IDLE: 'IDLE',
  SENDING: 'SENDING',
  SEND_ERROR: 'SEND_ERROR',
  SUCCESS: 'SUCCESS',
}

const currentState: Ref<keyof typeof STATES> = ref('LOADING')

// Methods
const formatDates = (dateIn: string, dateOut: string) => {
  const startDate = toRuralityDate().displayDate(dateIn)
  const endDate = toRuralityDate().displayDate(dateOut)
  return `${startDate} - ${endDate}`
}

const loadOutings = async () => {
  const userId = userStore.getUserId.value
  if (!userId) {
    currentState.value = 'IDLE'
    return
  }

  currentState.value = 'LOADING'
  const data = await getOutingsList()

  if (data.success) {
    outingsList.value = data.success.data.outings
    currentState.value = 'IDLE'
  } else if (data.error) {
    addCottageError.value = t('outings.favorite_modal.error_load')
    currentState.value = 'SEND_ERROR'
  }
}

const handleSelectOuting = async (outing: any) => {
  const userId = userStore.getUserId.value
  if (!userId) {
    addCottageError.value = t('outings.favorite_modal.error_auth')
    currentState.value = 'SEND_ERROR'
    return
  }

  currentState.value = 'SENDING'
  addCottageError.value = null

  const response = await addCottageToOuting({
    outingId: outing.id,
    cottageUid: props.item.id,
    note: null,
    createdBy: userId,
  })

  if (response.success) {
    currentState.value = 'SUCCESS'
    setTimeout(() => {
      emit('submitSuccess')
    }, 2000)
  } else if (response.error) {
    if (response.error.statusCode === 409) {
      addCottageError.value = t('outings.favorite_modal.error_already_added')
    } else {
      addCottageError.value = t('outings.favorite_modal.error_add')
    }
    currentState.value = 'SEND_ERROR'
  }
}

const init = async () => {
  await loadOutings()
}

const unsubscribe = watch(
  () => userStore.state.value.logged,
  () => {
    init()
  },
)

onUnmounted(() => {
  unsubscribe()
})

onMounted(async () => {
  init()
})
</script>
